import React, { ReactNode } from "react";
import styled from "styled-components";
import { Plus } from "lucide-react";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Button from "common/Buttons/Button";
import Tabs from "common/Tabs/Tabs";
import { usePageIntro } from "common/NewLook";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import useLatestRef from "features/portfolio/NewLook/useLatestRef.ts";

interface NewLookPortfolioIntroProps {
  activeTab?: string;
  tabsItems: { key: string; label: string }[];
  onTabChange: (key: string) => void;
  showBuy: boolean;
  loading: boolean;
  onBuyClick: () => void;
  /** The filters bar, shown before the Buy action. */
  filters?: ReactNode;
}

/**
 * Moves the assets / positions tabs, the filters and the Buy action into the
 * new-look page toolbar. Renders nothing itself.
 */
const NewLookPortfolioIntro = ({
  activeTab,
  tabsItems,
  onTabChange,
  showBuy,
  loading,
  onBuyClick,
  filters,
}: NewLookPortfolioIntroProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const onTabChangeRef = useLatestRef(onTabChange);
  const onBuyClickRef = useLatestRef(onBuyClick);

  usePageIntro(
    {
      actions: (
        <>
          {filters}
          {showBuy && (
            <Button
              type="primary"
              loading={loading}
              disabled={loading}
              onClick={() => onBuyClickRef.current()}
            >
              {!loading && (
                <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              )}
              {t("buttons.buy")}
            </Button>
          )}
        </>
      ),
      footer: (
        <IntroTabs
          activeKey={activeTab}
          onChange={(key: string) => onTabChangeRef.current(key)}
          items={tabsItems}
        />
      ),
    },
    [activeTab, tabsItems, showBuy, loading, filters, i18n.language]
  );

  return null;
};

export default NewLookPortfolioIntro;

const IntroTabs = styled(Tabs)`
  margin-top: 12px;
  &.ant-tabs-top > .ant-tabs-nav {
    margin: 0;
  }
  &.ant-tabs .ant-tabs-tab {
    padding: 4px 0 10px;
  }
`;
